import { useEffect, useRef, useCallback } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { useCombatStore } from '@/state/useCombatStore'
import { WEAPONS, WEAPON_ORDER, WEAPON_SLOT_KEYS } from './weaponTypes'
import type { WeaponClass } from './weaponTypes'
import { WeaponMesh } from './WeaponMesh'
import { Projectile, nextProjectileId } from './Projectile'
import type { ProjectileState } from './Projectile'
import { syncProjectiles } from './projectileRegistry'

const DODGE_SPEED = 800
const DODGE_DURATION = 0.25

// Multi-phase attack timing (fraction of total attack time)
const WINDUP_END = 0.20     // arm draws back
const SLASH_END = 0.55      // fast forward slash
const FOLLOWTHRU_END = 1.0  // decelerate + return

const _weaponPos = new THREE.Vector3()
const _weaponQ = new THREE.Quaternion()
const _swingQ = new THREE.Quaternion()
const _tiltQ = new THREE.Quaternion()
const _fwd = new THREE.Vector3()

// Rotate weapon geometry so +X (blade) points along character forward (-Z in lookAt)
// This is a -90 degree rotation around Y (local up)
const _bladeForwardQ = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), -Math.PI / 2)

interface CombatControllerProps {
  charRef: React.RefObject<THREE.Object3D | null>
  theta: number
  phi: number
  yaw: number
  normal: THREE.Vector3
  charPos: THREE.Vector3
  /** Character forward direction (world space) */
  charFwd: THREE.Vector3
  /** Character right direction (world space) */
  charRight: THREE.Vector3
  sphereRadius: number
  onDodge?: (dTheta: number, dPhi: number) => void
}

export function CombatController({
  charRef,
  theta,
  phi,
  yaw,
  normal,
  charPos,
  charFwd,
  charRight,
  sphereRadius,
  onDodge,
}: CombatControllerProps) {
  const weaponRef = useRef<THREE.Group>(null)
  const projectilesRef = useRef<ProjectileState[]>([])

  const equippedWeapon = useCombatStore((s) => s.equippedWeapon)
  const isDodging = useCombatStore((s) => s.isDodging)

  const startAttack = useCombatStore((s) => s.startAttack)
  const setAttackCooldown = useCombatStore((s) => s.setAttackCooldown)
  const setAttackTimer = useCombatStore((s) => s.setAttackTimer)
  const setIsAttacking = useCombatStore((s) => s.setIsAttacking)
  const triggerAbility = useCombatStore((s) => s.triggerAbility)
  const setAbilityCooldown = useCombatStore((s) => s.setAbilityCooldown)
  const setDodging = useCombatStore((s) => s.setDodging)
  const setDodgeTimer = useCombatStore((s) => s.setDodgeTimer)
  const setBlocking = useCombatStore((s) => s.setBlocking)
  const setPowerStriking = useCombatStore((s) => s.setPowerStriking)
  const setSteadyAiming = useCombatStore((s) => s.setSteadyAiming)
  const setReloading = useCombatStore((s) => s.setReloading)
  const setReloadTimer = useCombatStore((s) => s.setReloadTimer)
  const setHasAmmo = useCombatStore((s) => s.setHasAmmo)
  const setIsAbilityActive = useCombatStore((s) => s.setIsAbilityActive)
  const equipWeapon = useCombatStore((s) => s.equipWeapon)

  const mouseDownRef = useRef(false)
  const rightMouseRef = useRef(false)

  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (e.button === 0) {
      mouseDownRef.current = true
      startAttack()
    }
    if (e.button === 2) {
      rightMouseRef.current = true
      const weapon = useCombatStore.getState().equippedWeapon
      const info = WEAPONS[weapon]
      if (info.ability === 'block') {
        setBlocking(true)
      }
    }
  }, [startAttack, setBlocking])

  const handleMouseUp = useCallback((e: MouseEvent) => {
    if (e.button === 0) mouseDownRef.current = false
    if (e.button === 2) {
      rightMouseRef.current = false
      setBlocking(false)
    }
  }, [setBlocking])

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const key = e.key.toLowerCase()

    const slotIdx = WEAPON_SLOT_KEYS.indexOf(key as typeof WEAPON_SLOT_KEYS[number])
    if (slotIdx >= 0 && slotIdx < WEAPON_ORDER.length) {
      const weapon = WEAPON_ORDER[slotIdx]
      const state = useCombatStore.getState()
      if (state.unlockedWeapons.includes(weapon)) {
        equipWeapon(weapon)
      }
      return
    }

    if (key === 'r') {
      const state = useCombatStore.getState()
      const info = WEAPONS[state.equippedWeapon]

      if (triggerAbility()) {
        switch (info.ability) {
          case 'dodge_roll':
            setDodging(true)
            setDodgeTimer(DODGE_DURATION)
            break
          case 'power_strike':
            setPowerStriking(true)
            break
          case 'quick_reload':
            setReloading(false)
            setReloadTimer(0)
            setHasAmmo(true)
            startAttack()
            break
          case 'steady_aim':
            setSteadyAiming(true)
            break
          case 'block':
            setIsAbilityActive(false)
            break
        }
      }
    }
  }, [triggerAbility, setDodging, setDodgeTimer, setPowerStriking, setSteadyAiming,
      setReloading, setReloadTimer, setHasAmmo, startAttack, setIsAbilityActive, equipWeapon])

  const handleContextMenu = useCallback((e: Event) => e.preventDefault(), [])

  useEffect(() => {
    window.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('contextmenu', handleContextMenu)
    return () => {
      window.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('contextmenu', handleContextMenu)
    }
  }, [handleMouseDown, handleMouseUp, handleKeyDown, handleContextMenu])

  useFrame((_, delta) => {
    const dt = Math.min(delta, 0.1)
    const state = useCombatStore.getState()
    const info = WEAPONS[state.equippedWeapon]
    const attackDuration = 1 / info.attackSpeed * 0.75 // attack anim takes 75% of cycle, minimal cooldown gap

    // ── Attack cooldown ──
    if (state.attackCooldown > 0) {
      setAttackCooldown(Math.max(0, state.attackCooldown - dt))
    }

    // ── Ability cooldown ──
    if (state.abilityCooldown > 0) {
      setAbilityCooldown(Math.max(0, state.abilityCooldown - dt))
    }

    // ── Attack animation ──
    if (state.isAttacking) {
      const newTimer = state.attackTimer + dt
      setAttackTimer(newTimer)

      if (newTimer >= attackDuration) {
        setIsAttacking(false)
        setAttackTimer(0)
        setAttackCooldown(1 / info.attackSpeed - attackDuration)

        if (info.category === 'ranged' || info.category === 'thrown') {
          spawnProjectile(info, state, theta, phi, yaw, normal, charPos)
          setHasAmmo(false)
          setReloading(true)
          setReloadTimer(info.reloadTime ?? 1)
        }

        if (state.isPowerStriking) setPowerStriking(false)
        if (state.isSteadyAiming) setSteadyAiming(false)
      }
    }

    // ── Reload ──
    if (state.isReloading) {
      const newReload = state.reloadTimer - dt
      if (newReload <= 0) {
        setReloading(false)
        setReloadTimer(0)
        setHasAmmo(true)
      } else {
        setReloadTimer(newReload)
      }
    }

    // ── Dodge ──
    if (state.isDodging) {
      const newDodge = state.dodgeTimer - dt
      if (newDodge <= 0) {
        setDodging(false)
        setDodgeTimer(0)
      } else {
        setDodgeTimer(newDodge)
        if (onDodge) {
          const angularStep = (DODGE_SPEED * dt) / sphereRadius
          const sinPhi = Math.sin(phi)
          const cosPhi = Math.cos(phi)
          const cosYaw = Math.cos(yaw)
          const sinYaw = Math.sin(yaw)

          const cosTheta = Math.cos(theta)
          const sinTheta = Math.sin(theta)
          const northX = cosPhi * sinTheta, northY = -sinPhi
          const eastX = cosTheta

          const fwdX = -northX * cosYaw + eastX * sinYaw
          const fwdY = -northY * cosYaw

          onDodge(fwdX * angularStep, fwdY * angularStep)
        }
      }
    }

    // ── Update projectiles ──
    const projectiles = projectilesRef.current
    for (let i = projectiles.length - 1; i >= 0; i--) {
      const p = projectiles[i]
      p.age += dt

      const r = p.position.length()
      if (r > 0) {
        p.velocity.addScaledVector(
          p.position.clone().normalize(),
          -20 * dt,
        )
      }

      p.position.addScaledVector(p.velocity, dt)
      p.distanceTraveled += p.velocity.length() * dt

      if (p.age > 5 || p.distanceTraveled > p.maxRange || p.position.length() < sphereRadius - 1) {
        projectiles.splice(i, 1)
      }
    }

    // ── Sync projectiles for external hit detection ──
    syncProjectiles(projectiles)

    // ── Position weapon relative to character ──
    if (weaponRef.current && charRef.current) {
      // Hand position: right side, waist height, slightly forward
      // Use charFwd/charRight/normal directly — they're in world space and
      // match the character's actual orientation set by lookAt in FirstPersonCamera
      // charPos is at feet level — offset up to hand height
      _weaponPos.copy(charPos)
        .addScaledVector(charRight, 0.25)   // right hand
        .addScaledVector(normal, 1.05)      // hand height from feet
        .addScaledVector(charFwd, 0.05)     // slightly forward

      // Base orientation: character quaternion + rotate so blade (+X) faces forward
      _weaponQ.copy(charRef.current.quaternion).multiply(_bladeForwardQ)

      if (info.category === 'melee') {
        // ── Multi-phase melee swing ──
        if (state.isAttacking) {
          const t = state.attackTimer / attackDuration // 0..1
          let swingAngle: number  // rotation around normal (horizontal arc)
          let tiltAngle: number   // rotation around right (vertical tilt)

          if (t < WINDUP_END) {
            // Wind-up: draw sword back and up
            const p = t / WINDUP_END
            const ease = p * p // accelerating
            swingAngle = ease * 0.8    // pull back to the right
            tiltAngle = ease * -0.4    // raise slightly
            // Shift weapon back
            _weaponPos.addScaledVector(charRight, ease * 0.2)
            _weaponPos.addScaledVector(normal, ease * 0.3)
          } else if (t < SLASH_END) {
            // Slash: fast arc from right to left and downward
            const p = (t - WINDUP_END) / (SLASH_END - WINDUP_END)
            // Fast ease-in, slow ease-out (cubic)
            const ease = 1 - (1 - p) * (1 - p) * (1 - p)
            swingAngle = 0.8 - ease * 2.6  // sweep from +0.8 to -1.8
            tiltAngle = -0.4 + ease * 0.8  // comes down
            // Swing moves weapon forward and to the left
            _weaponPos.addScaledVector(charFwd, ease * 0.4)
            _weaponPos.addScaledVector(charRight, 0.2 - ease * 0.5)
          } else {
            // Follow-through: decelerate, return to rest
            const p = (t - SLASH_END) / (FOLLOWTHRU_END - SLASH_END)
            const ease = p * p // decelerating (into rest)
            swingAngle = -1.8 + ease * 1.8  // return to 0
            tiltAngle = 0.4 - ease * 0.4    // level out
            _weaponPos.addScaledVector(charFwd, 0.4 * (1 - ease))
            _weaponPos.addScaledVector(charRight, -0.3 * (1 - ease))
          }

          // Apply swing (around normal = horizontal slash)
          _swingQ.setFromAxisAngle(normal, swingAngle)
          _weaponQ.premultiply(_swingQ)

          // Apply tilt (around character right = vertical arc)
          _tiltQ.setFromAxisAngle(charRight, tiltAngle)
          _weaponQ.premultiply(_tiltQ)

        } else if (state.isBlocking) {
          // Block: hold weapon horizontally in front
          _weaponPos.addScaledVector(charFwd, 0.4)
          _weaponPos.addScaledVector(normal, 0.2)
          _swingQ.setFromAxisAngle(normal, -Math.PI * 0.35)
          _weaponQ.premultiply(_swingQ)

        } else if (state.isPowerStriking) {
          // Power strike ready: weapon raised high, glowing
          const bob = Math.sin(Date.now() * 0.008) * 0.05
          _weaponPos.addScaledVector(normal, 0.5 + bob)
          _weaponPos.addScaledVector(charRight, 0.15)
          _swingQ.setFromAxisAngle(charRight, -0.6)
          _weaponQ.premultiply(_swingQ)
        }

      } else {
        // ── Ranged weapon positioning ──
        // Recompute quaternion with blade-forward for ranged too
        _weaponQ.copy(charRef.current.quaternion).multiply(_bladeForwardQ)

        // Hold forward like aiming — charPos at feet, offset to chest
        _weaponPos.copy(charPos)
          .addScaledVector(charRight, 0.22)
          .addScaledVector(normal, 1.20)
          .addScaledVector(charFwd, 0.3)

        if (state.isAttacking) {
          // Recoil animation
          const t = state.attackTimer / attackDuration
          const recoil = Math.sin(t * Math.PI)
          _weaponPos.addScaledVector(charFwd, -recoil * 0.2)
          _weaponPos.addScaledVector(normal, recoil * 0.15)
          // Kick rotation
          _swingQ.setFromAxisAngle(charRight, -recoil * 0.3)
          _weaponQ.premultiply(_swingQ)
        }

        if (state.isSteadyAiming) {
          // Tighter aim position
          _weaponPos.addScaledVector(charFwd, 0.15)
          _weaponPos.addScaledVector(charRight, -0.1)
        }

        if (state.isReloading) {
          // Tilt down during reload
          _swingQ.setFromAxisAngle(charRight, 0.4)
          _weaponQ.premultiply(_swingQ)
          _weaponPos.addScaledVector(normal, -0.15)
        }
      }

      weaponRef.current.position.copy(_weaponPos)
      weaponRef.current.quaternion.copy(_weaponQ)
    }
  })

  function spawnProjectile(
    info: typeof WEAPONS[WeaponClass],
    state: ReturnType<typeof useCombatStore.getState>,
    theta: number, phi: number, yaw: number,
    normal: THREE.Vector3, charPos: THREE.Vector3,
  ) {
    const sinPhi = Math.sin(phi), cosPhi = Math.cos(phi)
    const sinTheta = Math.sin(theta), cosTheta = Math.cos(theta)
    const cosYaw = Math.cos(yaw), sinYaw = Math.sin(yaw)

    const northX = cosPhi * sinTheta, northY = -sinPhi, northZ = cosPhi * cosTheta
    const eastX = cosTheta, eastZ = -sinTheta

    _fwd.set(
      -northX * cosYaw + eastX * sinYaw,
      -northY * cosYaw,
      -northZ * cosYaw + eastZ * sinYaw,
    )

    const speed = info.projectileSpeed ?? 150
    const pos = charPos.clone().addScaledVector(normal, 1.2).addScaledVector(_fwd, 0.5)
    const vel = _fwd.clone().multiplyScalar(speed).addScaledVector(normal, speed * 0.15)

    let dmg = info.damage
    if (state.isPowerStriking) dmg *= 3
    if (state.isSteadyAiming) dmg *= 2

    projectilesRef.current.push({
      id: nextProjectileId(),
      position: pos,
      velocity: vel,
      age: 0,
      type: info.category === 'thrown' ? 'harpoon' : 'bullet',
      damage: dmg,
      maxRange: info.range,
      distanceTraveled: 0,
    })
  }

  return (
    <>
      <group ref={weaponRef}>
        <WeaponMesh weapon={equippedWeapon} visible={!isDodging} />
      </group>
      {projectilesRef.current.map((p) => (
        <Projectile key={p.id} projectile={p} />
      ))}
    </>
  )
}
